"use client";
import { useState } from "react";
import { Check, X, Home, Warehouse, Wifi, Fan, Car, PawPrint, Zap, Utensils, Dumbbell, Waves, Shield, Package, Accessibility, Building, Trees, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const AMENITIES_PER_PAGE = 6;

interface AmenityItem {
  name: string;
  available: boolean;
  icon: React.ComponentType<{ className?: string }>;
}

interface PropertyAmenitiesProps {
  amenitiesData?: Record<string, any>;
}

const PropertyAmenities = ({ amenitiesData = {} }: PropertyAmenitiesProps) => {
  const [page, setPage] = useState(1);

  const parseRealAmenities = (data: Record<string, any>): AmenityItem[] => {
    // Handle the actual database format: { selected: [...], by_category: {} }
    let selectedAmenities: string[] = [];
    
    if (data && Array.isArray(data.selected)) {
      selectedAmenities = data.selected;
    } else if (data && typeof data === 'object') {
      // Fallback: look for any array values in the object
      const arrayValues = Object.values(data).find(value => Array.isArray(value));
      if (arrayValues) {
        selectedAmenities = arrayValues as string[];
      }
    }

    if (selectedAmenities.length === 0) {
      return [];
    }

    // Enhanced icon mapping for real database amenities
    const getAmenityIcon = (amenityName: string): React.ComponentType<{ className?: string }> => {
      const name = amenityName.toLowerCase();
      
      if (name.includes('kitchen') || name.includes('oven') || name.includes('gas') || name.includes('refrigerator') || name.includes('freezer')) return Utensils;
      if (name.includes('gym') || name.includes('fitness')) return Dumbbell;
      if (name.includes('pool') || name.includes('swimming')) return Waves;
      if (name.includes('parking') || name.includes('garage') || name.includes('car')) return Car;
      if (name.includes('security') || name.includes('guard') || name.includes('gate')) return Shield;
      if (name.includes('elevator') || name.includes('lift')) return Warehouse;
      if (name.includes('storage') || name.includes('pantry')) return Package;
      if (name.includes('wheelchair') || name.includes('accessible') || name.includes('ramp')) return Accessibility;
      if (name.includes('balcony') || name.includes('terrace') || name.includes('porch')) return Building;
      if (name.includes('garden') || name.includes('playground') || name.includes('landscape')) return Trees;
      if (name.includes('wifi') || name.includes('internet') || name.includes('broadband')) return Wifi;
      if (name.includes('air') || name.includes('ac') || name.includes('cooling')) return Fan;
      if (name.includes('generator') || name.includes('power') || name.includes('electricity')) return Zap;
      if (name.includes('laundry') || name.includes('washing')) return Wifi;
      if (name.includes('community') || name.includes('social') || name.includes('lounge')) return Users;
      
      return Home; // Default icon
    };

    return selectedAmenities.map(amenity => ({
      name: amenity.trim(),
      available: true, // All selected amenities are available
      icon: getAmenityIcon(amenity)
    }));
  };

  const amenities = parseRealAmenities(amenitiesData);

  // Early return for empty amenities - no hardcoded fallbacks
  if (amenities.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="p-2 rounded-lg bg-gray-100">
            <Home className="h-4 w-4 text-gray-600" />
          </div>
          Amenities in This Property
        </h2>
        <div className="text-center py-6">
          <div className="p-3 rounded-full bg-gray-100 w-fit mx-auto mb-3">
            <Home className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm">
            No amenity information available for this property.
          </p>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(amenities.length / AMENITIES_PER_PAGE);
  const startIdx = (page - 1) * AMENITIES_PER_PAGE;
  const endIdx = startIdx + AMENITIES_PER_PAGE;
  const currentAmenities = amenities.slice(startIdx, endIdx);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <div className="p-2 rounded-lg bg-gray-100">
          <Home className="h-4 w-4 text-gray-600" />
        </div>
        Amenities in This Property
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {currentAmenities.map((amenity, i) => {
          const IconComponent = amenity.icon;
          return (
            <div
              key={startIdx + i}
              className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100"
            >
              <div className="p-2 rounded-lg bg-white border border-green-200">
                <IconComponent className="h-4 w-4 text-green-600" />
              </div>
              <span className="grow font-medium text-gray-900 text-sm">
                {amenity.name}
              </span>
              <Check className="h-4 w-4 text-green-600" />
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-xs text-gray-500 flex items-center">Page {page} of {totalPages}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default PropertyAmenities; 